import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'jsmCreateRequestInstruction'
	| 'jsmCreateRequestTitle'
	| 'summaryLabel'
	| 'descriptionLabel'
	| 'createRequestButton'
	| 'jsmRequestCreatedTitle'
	| 'jsmRequestCreatedDescription'
	| 'jsmRequestErrorTitle'
	| 'jsmRequestErrorDescription'
	| 'additionalFieldsLabel'
	| 'fieldNotSet';

const message: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		jsmCreateRequestInstruction: {
			id: 'conversation-assistant-message-actions.jsm-create-request.instruction-1',
			defaultMessage:
				'Review and edit the details below, then click {createButtonCopy} to raise your request.',
			description:
				'Instruction text displayed in the Jira Service Management (JSM) request creation action card within the AI conversation interface, providing guidance on the next steps. Shown as instructional text in the action card body. The createButtonCopy placeholder will be substituted with the text of the create button (e.g., "Raise request", "Create request"). The purpose is to guide users to review the AI-suggested request details and then proceed with raising the request using the button.',
		},
		jsmCreateRequestTitle: {
			id: 'conversation-assistant-message-actions.jsm-create-request.title',
			defaultMessage: 'Create request',
			description:
				'Title text displayed at the top of an action card in the AI conversation interface when the AI suggests creating a new Jira Service Management (JSM) service request. Shown as the card header title. The purpose is to immediately communicate to the user what action the card represents, which is creating a new service request in JSM.',
		},
		summaryLabel: {
			id: 'conversation-assistant-message-actions.jsm-create-request.summary-label',
			defaultMessage: 'Summary',
			description:
				'Label text displayed above or next to a summary input field in the Jira Service Management (JSM) request creation form within the AI conversation interface. Shown as a form field label. The purpose is to identify the summary input field, which allows users to enter or edit a brief summary of the service request.',
		},
		descriptionLabel: {
			id: 'conversation-assistant-message-actions.jsm-create-request.description-label',
			defaultMessage: 'Description',
			description:
				'Label text displayed above or next to a description input field in the Jira Service Management (JSM) request creation form within the AI conversation interface. Shown as a form field label. The purpose is to identify the description input field, which allows users to enter or edit detailed information about the service request.',
		},
		createRequestButton: {
			id: 'conversation-assistant-message-actions.jsm-create-request.create-request-button-1',
			defaultMessage: 'Raise request',
			description:
				'Button label displayed on the primary action button in the Jira Service Management (JSM) request creation action card within the AI conversation interface. Shown as the text on a button at the bottom of the action card. When clicked, it creates and submits the service request to JSM. The purpose is to provide a clear call-to-action for users to confirm and execute creating the service request.',
		},
		jsmRequestCreatedTitle: {
			id: 'conversation-assistant-message-actions.jsm-create-request.created-title-1',
			defaultMessage: 'Request raised',
			description:
				'Title text displayed at the top of a success notification or dialog after a Jira Service Management (JSM) service request has been successfully created within the AI conversation interface. Shown as the notification or dialog header title. The purpose is to immediately communicate to the user that the request creation was successful.',
		},
		jsmRequestCreatedDescription: {
			id: 'conversation-assistant-message-actions.jsm-create-request.created-description-1',
			defaultMessage: 'Your service request has been raised successfully.',
			description:
				'Success message text displayed in the body of a success notification or dialog after a Jira Service Management (JSM) service request has been successfully created within the AI conversation interface. Shown as the description text below the success title. The purpose is to confirm the successful creation of the service request and inform the user that their request is now active.',
		},
		jsmRequestErrorTitle: {
			id: 'conversation-assistant-message-actions.jsm-create-request.error-title-1',
			defaultMessage: 'Error raising request',
			description:
				'Title text displayed at the top of an error notification or dialog when a Jira Service Management (JSM) service request creation fails within the AI conversation interface. Shown as the error notification or dialog header title. The purpose is to immediately communicate to the user that an error occurred during request creation.',
		},
		jsmRequestErrorDescription: {
			id: 'conversation-assistant-message-actions.jsm-create-request.error-description-1',
			defaultMessage: 'There was an error raising your service request. Please try again.',
			description:
				'Error message text displayed in the body of an error notification or dialog when a Jira Service Management (JSM) service request creation fails within the AI conversation interface. Shown as the error description text below the error title. The purpose is to inform the user that the request creation encountered an error and prompt them to retry the action.',
		},
		additionalFieldsLabel: {
			id: 'conversation-assistant-message-actions.jsm-create-request.additional-fields-label',
			defaultMessage: 'Additional Fields:',
			description:
				'Label text displayed above a section containing additional custom fields in the Jira Service Management (JSM) request creation form within the AI conversation interface. Shown as a section label or heading. The purpose is to identify the section that contains extra fields beyond the standard summary and description fields, such as custom fields specific to the request type or service project.',
		},
		fieldNotSet: {
			id: 'conversation-assistant-message-actions.jsm-create-request.field-not-set',
			defaultMessage: 'Not set',
			description:
				'Placeholder text displayed when an additional field in the Jira Service Management (JSM) request creation form has no value. Shown as the field value text. The purpose is to indicate to the user that the field currently has no value assigned.',
		},
	});

export default message;
